import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsElectronContext, useElectronVersion, useIsLegacyElectron } from '../../context/ElectronContext';

import './LegacyWarning.scss';

const MIN_MODERN_VERSION = '2.0.0';
const DISMISS_STORAGE_KEY = 'legacy-electron-warning-dismissed';

/**
 * Componente que muestra un aviso cuando la app de escritorio
 * es legacy o tiene una versión inferior a 2.0.0
 * 
 * Se muestra automáticamente al detectar la API legacy
 * y puede descartarse temporalmente (hasta la próxima sesión)
 */
export function LegacyElectronWarning() {
  const { t } = useTranslation();
  const isElectron = useIsElectronContext();
  const isLegacy = useIsLegacyElectron();
  const { version, loading: versionLoading } = useElectronVersion();
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // No mostrar si no estamos en Electron
    if (!isElectron) return;
    // No mostrar mientras carga la versión  
    if (versionLoading) return;
    // Si ya fue descartada en esta sesión
    if (sessionStorage.getItem(DISMISS_STORAGE_KEY)) return;

    // Mostrar si es legacy O si la versión es < 2.0.0
    if (isLegacy) {
      setVisible(true);
      return;
    }

    if (version) {
      const parts = version.replace(/^v/i, '').split('.').map(Number);
      const minParts = MIN_MODERN_VERSION.split('.').map(Number);
      const isOld = (parts[0] || 0) < (minParts[0] || 0)
        || ((parts[0] || 0) === (minParts[0] || 0) && (parts[1] || 0) < (minParts[1] || 0));
      if (isOld) {
        setVisible(true);
      }
    }
  }, [isElectron, isLegacy, version, versionLoading]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    sessionStorage.setItem(DISMISS_STORAGE_KEY, 'true');
    // Animar salida
    setTimeout(() => setVisible(false), 300);
  }, []);

  if (!visible) return null;

  return (
    <div className={`legacy-warning-overlay ${dismissed ? 'dismissing' : ''}`}>
      <div className="legacy-warning-modal">
        {/* Header con icono */}
        <div className="legacy-warning-modal__header">
          <div className="legacy-warning-modal__icon">
            <i className="las la-exclamation-triangle" />
          </div>
          <h2>{t('modal.legacy.title')}</h2>
        </div>

        {/* Contenido */}
        <div className="legacy-warning-modal__body">
          <p>{t('modal.legacy.description')}</p>

          <div className="legacy-warning-modal__version-info">
            <div className="version-row">
              <span className="version-label">{t('modal.legacy.current_version')}</span>
              <span className="version-value legacy">
                {version || t('modal.legacy.unknown_version')}
              </span>
            </div>
            <div className="version-row">
              <span className="version-label">{t('modal.legacy.required_version')}</span>
              <span className="version-value modern">{MIN_MODERN_VERSION}+</span>
            </div>
          </div>

          <p className="legacy-warning-modal__note">
            <i className="las la-info-circle" />
            {t('modal.legacy.note')}
          </p>
        </div>

        {/* Botones */}
        <div className="legacy-warning-modal__actions">
          <button className="btn-dismiss" onClick={handleDismiss}>
            {t('modal.legacy.dismiss')}
          </button>
        </div>
      </div>
    </div>
  );
}
